<script>
  import { onMount, getContext } from "svelte";
  import { _, contract } from "../../stores/index";
  import {
    RolesRepository,
    PoliciesRepository,
    ContractsRepository,
  } from "../../repositories/index";
  import { Select, SelectItem, Flex, FlexItem } from "@gl/design-system";
  import { parseUrl, setUrl } from "../../utils/index";

  const { isAuthorized } = getContext("isAuthorized");

  export let role;

  let loading = { all: true };
  let mainRole = null;
  let availableRoles = [];
  let availableApps = [];
  let allPolicies = [];
  let comparingRoles = [];

  let policyRows = [];
  let rolePolicySets = {};

  let applicationView = "all";

  const ROLE_COLORS = ["#002735", "#003d54", "#00678d"];

  $: displayedRoles = [mainRole, ...comparingRoles];
  $: filteredRows =
    applicationView === "all"
      ? policyRows
      : policyRows.filter((row) => row.appId === applicationView);

  async function fetchRoles() {
    const result = await RolesRepository.getRoles();
    availableRoles = result.list.filter((r) => r.id !== role.id);
  }

  async function fetchApps() {
    if (!$isAuthorized("app_read")) return;
    const result = await ContractsRepository.getContractApps($contract?.id);
    availableApps = result.list;
  }

  async function fetchPolicies() {
    const result = await PoliciesRepository.getPoliciesOfContract();
    allPolicies = result.list;
  }

  async function fetchRolePolicies(roleId) {
    const result = await RolesRepository.getRolePolicies(roleId);
    rolePolicySets[roleId] = new Set(result.list);
    rolePolicySets = rolePolicySets;
  }

  function buildPolicyRows() {
    const rowMap = new Map();

    for (const policy of allPolicies) {
      const key = `${policy.obj}|${policy.appId}`;

      if (!rowMap.has(key)) {
        rowMap.set(key, {
          key,
          obj: policy.obj,
          appId: policy.appId,
          appName: policy.appName,
          appType: policy.appType,
          actions: new Map(),
        });
      }

      const row = rowMap.get(key);
      row.actions.set(policy.act, {
        policyId: policy.id,
        implicitFrom: [],
      });
    }

    for (const policy of allPolicies) {
      if (!policy.implicit) continue;

      const implicitNames = policy.implicit.split(",").map((s) => s.trim());

      for (const implicitName of implicitNames) {
        const implicitPolicy = allPolicies.find(
          (p) => p.name === implicitName && p.appId === policy.appId,
        );
        if (!implicitPolicy) continue;

        const key = `${implicitPolicy.obj}|${implicitPolicy.appId}`;
        const row = rowMap.get(key);
        if (!row) continue;

        const action = row.actions.get(implicitPolicy.act);
        if (action && !action.implicitFrom.includes(policy.id)) {
          action.implicitFrom.push(policy.id);
        }
      }
    }

    // Convert to array and sort
    policyRows = Array.from(rowMap.values()).map((row) => ({
      ...row,
      actions: Array.from(row.actions.entries()).map(([name, data]) => ({
        name,
        ...data,
      })),
    }));
  }

  function getRoleActions(row, roleId) {
    if (!roleId || !rolePolicySets[roleId]) return [];

    const policies = rolePolicySets[roleId];
    const result = [];

    for (const action of row.actions) {
      const hasExplicit = policies.has(action.policyId);
      const hasImplicit =
        !hasExplicit && action.implicitFrom.some((id) => policies.has(id));

      if (hasExplicit || hasImplicit) {
        result.push({
          name: action.name,
          isImplicit: hasImplicit,
        });
      }
    }

    return result;
  }

  function updateUrl() {
    setUrl({
      applicationView,
      roles: comparingRoles.map((r) => r?.id).filter(Boolean),
    });
  }

  async function parseUrlParams() {
    const query = parseUrl(window.location.search);
    applicationView = query.applicationView || "all";

    if (query.roles) {
      const roleIds = query.roles.split(",");
      comparingRoles = roleIds.map((id) =>
        availableRoles.find((r) => r.id === id),
      );

      for (const r of comparingRoles) {
        if (r) await fetchRolePolicies(r.id);
      }
    }
  }

  async function handleRoleSelect() {
    for (const r of comparingRoles) {
      if (r && !rolePolicySets[r.id]) {
        await fetchRolePolicies(r.id);
      }
    }

    buildPolicyRows();

    updateUrl();
  }

  function handleAppFilterChange() {
    updateUrl();
  }

  function areActsDifferentFromMainRole(row, roleId) {
    if (!roleId || !mainRole || roleId === mainRole.id) return false;

    const mainActions = getRoleActions(row, mainRole.id);
    const roleActions = getRoleActions(row, roleId);

    const mainActionNames = new Set(mainActions.map((a) => a.name));
    const roleActionNames = new Set(roleActions.map((a) => a.name));

    for (const name of roleActionNames) {
      if (!mainActionNames.has(name)) return true;
    }

    for (const name of mainActionNames) {
      if (!roleActionNames.has(name)) return true;
    }

    return false;
  }

  function isActDifferentFromMainRole(row, action) {
    const mainActions = getRoleActions(row, mainRole.id);
    const mainActionNames = new Set(mainActions.map((a) => a.name));
    return !mainActionNames.has(action.name);
  }

  async function initialize() {
    loading.all = true;
    try {
      await Promise.all([fetchRoles(), fetchPolicies(), fetchApps()]);

      mainRole = { ...role.data, id: role.id };
      await fetchRolePolicies(role.id);

      buildPolicyRows();
      await parseUrlParams();
    } finally {
      loading.all = false;
    }
  }

  onMount(initialize);
</script>

{#if !loading.all}
  <Flex class="align-items-center">
    <!-- App Filter -->
    {#if availableApps.length}
      <FlexItem>
        <Select bind:value={applicationView} on:change={handleAppFilterChange}>
          <SelectItem
            label={`${$_("common.apps")}: ${$_("common.all")}`}
            value="all"
          />
          {#each availableApps as app}
            <SelectItem
              label={`${$_("common.apps")}: ${app.name} (${app.type})`}
              value={app.id}
            />
          {/each}
        </Select>
      </FlexItem>
    {/if}

    <div class="vertical-line mx-3" />

    <!-- Role Selectors -->
    {#each [0, 1] as index}
      <FlexItem class="mr-3">
        <Select bind:value={comparingRoles[index]} on:change={handleRoleSelect}>
          {#each availableRoles as r}
            <SelectItem label={r.name} value={r} />
          {/each}
        </Select>
      </FlexItem>
    {/each}
  </Flex>

  <div class="table-scroll my-5">
    <table>
      <thead>
        <tr>
          <th class="header-app">{$_("common.app.type")}</th>
          <th class="header-policy">{$_("common.policy")}</th>
          {#each displayedRoles as displayRole, index}
            <th class="header-role" style:--role-color={ROLE_COLORS[index]}>
              {displayRole?.name ?? "-"}
            </th>
          {/each}
        </tr>
      </thead>
      <tbody>
        {#each filteredRows as row}
          <tr>
            <td class="cell-app">
              {row.appName}
              <p class="text-light">({row.appType})</p>
            </td>
            <td class="cell-policy">{row.obj}</td>
            {#each displayedRoles as displayRole, index}
              <td
                class="cell-actions"
                style:--role-color={ROLE_COLORS[index]}
                class:background-color-info-light={displayRole &&
                  areActsDifferentFromMainRole(row, displayRole.id)}
              >
                <div class="actions-container">
                  {#each getRoleActions(row, displayRole?.id) as action}
                    <span
                      class:implicit={action.isImplicit}
                      class:color-info-dark={isActDifferentFromMainRole(
                        row,
                        action,
                      )}
                    >
                      {action.name},
                    </span>
                  {/each}
                </div>
              </td>
            {/each}
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<style>
  .table-scroll {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    padding: 10px;
    width: 20%;
    max-width: 200px;
  }

  td {
    background-color: var(--gl-background);
    border-top: 1px solid var(--gl-border);
    border-bottom: 1px solid var(--gl-border);
  }

  th {
    font-weight: bold;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .header-app,
  .header-policy {
    background-color: var(--gl-background);
  }

  .header-policy {
    border-left: 1px solid var(--gl-border);
  }

  .header-role {
    background-color: var(--role-color);
    color: white;
    border-right: 1px solid var(--role-color);
  }

  .cell-policy {
    border-left: 1px solid var(--gl-border);
  }

  .cell-actions {
    border-left: 1px solid var(--role-color);
  }

  .actions-container {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 8px;
  }

  .implicit {
    color: var(--gl-text-disabled);
  }
</style>
